import { useCallback, useEffect } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  computeExpenseLineItemTotalsUseCase,
  type TaxType,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import { type ExpenseLineItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemForm'
import {
  ExpenseFormReimbursementCustomizations,
  type ExpenseFormReimbursementCustomization,
} from '@webapp/modules/expense/entities/enums'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import messages from './messages'
import { useMutateEditExpenseLineItem } from '../../models/useMutateEditExpenseLineItem'

interface UseEditExpenseLineItemPresenterParams {
  item: ExpenseItem
  expenseReportId: number
  reimbursementSetting: ExpenseFormReimbursementCustomization
  onSuccess: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
}

interface UseEditExpenseLineItemPresenterReturn {
  commands: {
    handleSubmit: (values: ExpenseLineItemFormSchema) => Promise<void>
  }
  states: {
    isSubmitting: boolean
  }
}

const buildExpenseTypeFields = (
  values: ExpenseLineItemFormSchema
): Record<string, unknown> => {
  switch (values.expenseType) {
    case ExpenseTypes.MEALS:
      return { tips: values.tips }
    case ExpenseTypes.MILEAGE:
      return {
        mileage_rate: values.mileageRate,
        distance: values.distance,
        unit_of_distance: values.unitOfDistanceStr.value,
      }
    case ExpenseTypes.LABOUR:
      return {
        hourly_rate: values.hourlyRate,
        hours_of_work: values.hoursOfWork,
      }
    case ExpenseTypes.AIRFARE:
      return {
        airline: values.airline,
        ticket_number: values.ticketNumber,
        departure_date: values.departureDate
          ? values.departureDate.format('YYYY-MM-DD')
          : null,
        arrival_date: values.arrivalDate
          ? values.arrivalDate.format('YYYY-MM-DD')
          : null,
      }
    default:
      return {}
  }
}

const getEffectiveReimbursable = (
  reimbursable: boolean,
  reimbursementSetting: ExpenseFormReimbursementCustomization
): boolean => {
  if (
    reimbursementSetting === ExpenseFormReimbursementCustomizations.ALWAYS_YES
  )
    return true
  if (reimbursementSetting === ExpenseFormReimbursementCustomizations.ALWAYS_NO)
    return false
  return reimbursable
}

const getTaxFields = (
  values: ExpenseLineItemFormSchema,
  computedTaxAmount: number
) => {
  const taxTypeName = values.taxType.value
  const isPercentage = taxTypeName === '%'

  return {
    tax_percentage: isPercentage
      ? (values.taxAmount / 100).toFixed(8)
      : '0.00000000',
    tax_value: computedTaxAmount.toFixed(2),
    tax_name: taxTypeName,
    tax_id: null as Nullable<number>,
    tax_is_inclusive: false,
  }
}

const getPaymentFields = (
  values: ExpenseLineItemFormSchema,
  effectiveReimbursable: boolean
) => {
  if (effectiveReimbursable) {
    return {
      paymentType: ExpensePaymentTypes.OTHER,
      creditcard: null,
    }
  }

  const hasRealCard =
    values.paymentMethod != null && values.paymentMethod.id !== -1
  const paymentType = hasRealCard
    ? ExpensePaymentTypes.CREDIT_CARD
    : ExpensePaymentTypes.OTHER

  return {
    paymentType,
    creditcard: hasRealCard ? values.paymentMethod.id : null,
  }
}

export const useEditExpenseLineItemPresenter = ({
  item,
  expenseReportId,
  reimbursementSetting,
  onSuccess,
  onSubmittingChange,
}: UseEditExpenseLineItemPresenterParams): UseEditExpenseLineItemPresenterReturn => {
  const intl = useProcurifyIntl()
  const { mutate, isLoading } = useMutateEditExpenseLineItem()

  useEffect(() => {
    onSubmittingChange?.(isLoading)
  }, [isLoading, onSubmittingChange])

  const handleSubmit = useCallback(
    async (values: ExpenseLineItemFormSchema) => {
      const { computedSubtotal, computedTaxAmount, computedTotal } =
        computeExpenseLineItemTotalsUseCase.execute({
          expenseType: values.expenseType,
          subtotal: values.subtotal,
          taxAmount: values.taxAmount,
          taxType: values.taxType.value as TaxType,
          tips: values.tips,
          mileageRate: values.mileageRate,
          distance: values.distance,
          hourlyRate: values.hourlyRate,
          hoursOfWork: values.hoursOfWork,
        })

      const effectiveReimbursable = getEffectiveReimbursable(
        values.reimbursable,
        reimbursementSetting
      )
      const taxFields = getTaxFields(values, computedTaxAmount)
      const paymentFields = getPaymentFields(values, effectiveReimbursable)

      try {
        await mutate({
          id: item.id,
          merchant: values.merchant,
          account: values.account?.id ?? item.account.id,
          expenseReport: expenseReportId,
          transDate: values.transDate
            ? values.transDate.format('YYYY-MM-DD')
            : '',
          subtotal: parseFloat(computedSubtotal.toFixed(2)),
          amount: computedTotal.toFixed(2),
          reimburse: String(effectiveReimbursable),
          expense_type: values.expenseType,
          expense_type_fields: buildExpenseTypeFields(values),
          localCurrency: item.currency.id,
          currency_rate_final: item.currency.rate,
          ...taxFields,
          ...paymentFields,
          attachment: values.attachment,
          notes: values.notes,
          exchange_rate_override: false,
          has_default_tax: false,
        })

        window.analytics?.track('PendingExpenseItem Edited', {
          editor: 'approver',
        })

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage),
        })

        onSuccess()
      } catch (err) {
        const errorMessage =
          getMessageFromError(err) ?? intl.formatMessage(messages.errorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [item, expenseReportId, intl, mutate, onSuccess, reimbursementSetting]
  )

  return {
    commands: {
      handleSubmit,
    },
    states: {
      isSubmitting: isLoading,
    },
  }
}
